import {
    ActionReducerMapBuilder,
    createAsyncThunk,
    createSlice,
    SerializedError,
} from '@reduxjs/toolkit';
import { urlUserLogout, urlUserProfile } from '../../Constants';
import { IAction, IAppReduxStore, SliceProps, SlicePropsWithData } from '../store';
import { apiFetch } from '../../util/ProjectUtils';

/**
 * Weekdays on which the user will receive email notifications. (1=sunday, 2=monday,...)
 * Array notificationWeekDays means: Monday to Friday
 */
const notificationWeekDays = [2, 3, 4, 5, 6];
/**
 * Application backend writes this variable to the page on load.
 * This global variable contains the URL that is used to log the user out of the application in Auth0.
 */
declare const auth0LogoutUri: string;

/**
 * Initial slice state value.
 */
const initialState: IUserProfileState = {
    data: null,
    loading: false,
    error: null,
    picture: {
        loading: false,
        error: null,
    },
    validatePassword: {
        data: false,
        loading: false,
        error: null,
    },
    resetPassword: {
        data: false,
        loading: false,
        error: null,
    },
    maxProfilePictureSize: 2621440,
    isLogoutPending: false,
};

/**
 * Collection of the reducers for this slice.
 */
const reducers = {
    resetWorkflowPasswordSuccess(state: IUserProfileState) {
        state.validatePassword.data = false;
    },
    resetWorkflowPasswordError(state: IUserProfileState) {
        state.validatePassword.error = null;
    },
    resetWorkflowPasswordResetSuccess(state: IUserProfileState) {
        state.resetPassword.data = false;
    },
    resetWorkflowPasswordResetError(state: IUserProfileState) {
        state.resetPassword.error = null;
    },
    updateUserWfPasswordActive(state: IUserProfileState, action: IAction<boolean>) {
        state.data.isApprovalPasswordActive = action.payload;
    },
    updateUserLastWfPasswordChange(state: IUserProfileState, action: IAction<string>) {
        state.data.lastApprovalPasswordChange = action.payload;
    },
};

/**
 * Collection of the thunks for this slice, along with their reducer functions.
 */
const thunks = {
    /**
     * Fetches the user's settings from the backend.
     */
    getUserProfile: async (): Promise<IUserData> => {
        const response = await apiFetch(urlUserProfile);

        return response.ok
            ? Promise.resolve(await response.json())
            : Promise.reject(new Error(await response.text()));
    },

    /**
     * Logs the user out of the application.
     */
    logoutUser: async (): Promise<void> => {
        const request: RequestInit = {
            method: 'POST',
        };
        const response = await fetch(urlUserLogout, request);

        return response.ok ? Promise.resolve() : Promise.reject(response.status);
    },

    /**
     * Updates the user's protection data in the backend.
     */
    updateUserProfileProtection: async (protect: string): Promise<string> => {
        try {
            /** Note: Backend only accepts data in form-format! */
            const formData = new FormData();
            formData.append('protect', protect);

            const request: RequestInit = {
                method: 'POST',
                body: formData,
            };
            const url = `${urlUserProfile}/protection`;
            const response = await apiFetch(url, request);

            return response.ok ? Promise.resolve(protect) : Promise.reject(response.status);
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    updateUserProfileReceiveTasksByEmail: async (
        receiveTasksByEmail: boolean,
    ): Promise<boolean> => {
        try {
            const daysToReceiveTasksByEmail = receiveTasksByEmail
                ? notificationWeekDays.join(';')
                : '[]';

            const formData = new FormData();
            formData.append('notificationDays', daysToReceiveTasksByEmail);

            const request: RequestInit = {
                method: 'POST',
                body: formData,
            };
            const url = `${urlUserProfile}/notifications`;
            const response = await apiFetch(url, request);

            return response.ok
                ? Promise.resolve(receiveTasksByEmail)
                : Promise.reject(new Error(response.status.toString()));
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    /**
     * Updates the user's picture in the backend.
     */
    updateUserProfilePicture: async (image: File, thunkApi): Promise<string> => {
        try {
            if (
                image.size >
                (thunkApi.getState() as IAppReduxStore).userProfile.maxProfilePictureSize
            ) {
                return Promise.reject('Failed to upload image, image size is more than 2,5MB.');
            }

            /** Note: Backend only accepts data in form-format! */
            const formData = new FormData();
            formData.append('image', image);

            const request: RequestInit = {
                method: 'POST',
                body: formData,
            };

            const url = `${urlUserProfile}/image`;
            const response = await apiFetch(url, request);
            const pictureData = await response.text();

            return pictureData
                ? Promise.resolve(pictureData)
                : Promise.reject(new Error('No picture data.'));
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    updateUserApprovalPassword: async (config: {
        oldPassword: string;
        newPassword: string;
        timestamp: string;
    }): Promise<void> => {
        try {
            const { oldPassword, newPassword, timestamp } = config;

            const formData = new FormData();
            formData.append('oldPassword', oldPassword);
            formData.append('newPassword', newPassword);
            formData.append('timestamp', timestamp);

            const request: RequestInit = {
                method: 'POST',
                body: formData,
            };

            const url = `${urlUserProfile}/workflow/approval`;
            const response = await apiFetch(url, request);

            return response.ok
                ? Promise.resolve()
                : Promise.reject(new Error(await response.text()));
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    updateUserApprovalPasswordToggle: async (isActive: boolean): Promise<void> => {
        try {
            const formData = new FormData();
            formData.append('isActive', isActive.toString());

            const request: RequestInit = {
                method: 'POST',
                body: formData,
            };

            const url = `${urlUserProfile}/workflow/approval/toggle`;
            const response = await apiFetch(url, request);

            return response.ok
                ? Promise.resolve()
                : Promise.reject(new Error(await response.text()));
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    resetUserApprovalPassword: async (): Promise<void> => {
        try {
            const request: RequestInit = {
                method: 'POST',
            };

            const url = `${urlUserProfile}/workflow/approval/reset`;
            const response = await apiFetch(url, request);

            return response.ok
                ? Promise.resolve()
                : Promise.reject(new Error(await response.text()));
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    /**
     * Registers the 'extraReducers' functions for the slice's thunks.
     */
    _reducers: (builder: ActionReducerMapBuilder<IUserProfileState>) => {
        const onLoad = (state: IUserProfileState) => {
            state.error = null;
            state.loading = true;
        };

        const onError = (state: IUserProfileState, action: IAction<SerializedError>) => {
            state.loading = false;
            state.data = null;
            state.error = action.error;
        };

        /**
         * Reducers for the 'getUserProfile' thunk.
         */
        builder.addCase(getUserProfile.pending, onLoad);
        builder.addCase(
            getUserProfile.fulfilled,
            (state: IUserProfileState, action: IAction<IUserData>) => {
                state.data = action.payload;
                state.error = null;
                state.loading = false;
            },
        );
        builder.addCase(getUserProfile.rejected, onError);

        /**
         * Reducers for the 'logoutUser' thunk.
         */
        builder.addCase(logoutUser.pending, (state: IUserProfileState) => {
            state.isLogoutPending = true;
            onLoad(state);
        });
        builder.addCase(logoutUser.fulfilled, (state: IUserProfileState) => {
            state.isLogoutPending = false;
            window.location.href = auth0LogoutUri;
        });
        builder.addCase(
            logoutUser.rejected,
            (state: IUserProfileState, action: IAction<SerializedError>) => {
                state.isLogoutPending = false;
                onError(state, action);
            },
        );

        /**
         * Reducers for the 'updateUserProfileProtection' thunk.
         */
        builder.addCase(
            updateUserProfileProtection.fulfilled,
            (state: IUserProfileState, action: IAction<string>) => {
                state.data.dataprotection = action.payload;
            },
        );
        builder.addCase(updateUserProfileProtection.rejected, onError);

        /**
         * Reducers for the 'updateUserProfileReceiveTasksByEmail' thunk.
         */
        builder.addCase(
            updateUserProfileReceiveTasksByEmail.fulfilled,
            (state: IUserProfileState, action: IAction<boolean>) => {
                state.data.notificationDays = action.payload ? notificationWeekDays : [];
            },
        );
        builder.addCase(updateUserProfileReceiveTasksByEmail.rejected, onError);

        /**
         * Reducers for the 'updateUserProfilePicture' thunk.
         */
        builder.addCase(updateUserProfilePicture.pending, (state: IUserProfileState) => {
            state.picture.loading = true;
        });
        builder.addCase(
            updateUserProfilePicture.fulfilled,
            (state: IUserProfileState, action: IAction<string>) => {
                state.data.picture = action.payload;
                state.picture.loading = false;
            },
        );
        builder.addCase(
            updateUserProfilePicture.rejected,
            (state: IUserProfileState, action: IAction<SerializedError>) => {
                state.picture.error = action.error;
                state.picture.loading = false;
            },
        );

        /**
         * Reducers for the 'updateUserApprovalPassword' thunk.
         */
        builder.addCase(updateUserApprovalPassword.pending, (state: IUserProfileState) => {
            state.validatePassword.loading = true;
        });
        builder.addCase(updateUserApprovalPassword.fulfilled, state => {
            state.validatePassword.data = true;
            state.validatePassword.loading = false;
            state.validatePassword.error = null;
        });
        builder.addCase(
            updateUserApprovalPassword.rejected,
            (state: IUserProfileState, action: IAction<SerializedError>) => {
                state.validatePassword.error = action.error;
                state.validatePassword.loading = false;
            },
        );
        /**
         * Reducers for the 'resetUserApprovalPassword' thunk.
         */
        builder.addCase(resetUserApprovalPassword.pending, (state: IUserProfileState) => {
            state.resetPassword.loading = true;
        });
        builder.addCase(resetUserApprovalPassword.fulfilled, state => {
            state.resetPassword.data = true;
            state.resetPassword.loading = false;
            state.resetPassword.error = null;
        });
        builder.addCase(
            resetUserApprovalPassword.rejected,
            (state: IUserProfileState, action: IAction<SerializedError>) => {
                state.resetPassword.error = action.error;
                state.resetPassword.loading = false;
            },
        );
    },
};

/**
 * Interface that describes the state object for this redux slice.
 */
export type IUserProfileState = SlicePropsWithData<IUserData> & {
    /** Max size in Bytes of image that is allowed for downloading. */
    maxProfilePictureSize: number;

    /** Defines loading and error state for the user avatar. */
    picture: SliceProps;

    /** User workflow password info. */
    validatePassword: SlicePropsWithData<boolean>;

    /** User reset password info. */
    resetPassword: SlicePropsWithData<boolean>;

    isLogoutPending: boolean;
};

/**
 * Interface that describes the metadata returned from Auth0 for the user.
 */
export interface IUserData {
    /** Custom fields that store info about a user that influences the user's access. */
    app_metadata: unknown;

    /** Indicates whether the user has been blocked. */
    blocked: boolean;

    /** Timestamp indicating when the user profile was first created. */
    created_at: string;

    /** (unique) The user's email address. */
    email: string;

    /** Indicates whether the user has verified their email address. */
    email_verified: boolean;

    /** The user's family name. */
    family_name: string;

    /** The user's given name. */
    given_name: string;

    /** Custom fields that store info about a user that does not impact what they can or cannot access. */
    user_metadata: unknown;

    /** (unique) The user's username. */
    username: string;

    /** The user's first_name. */
    first_name: string;

    /** The user's last_name. */
    last_name: string;

    /** Permission to use information. */
    dataprotection: string;

    /** Determines if the user is a shared system user. */
    isSystemuser: boolean;

    /** Contains info retrieved from the identity provider with which the user originally authenticates. */
    identities: {
        /** Name of the Auth0 connection used to authenticate the user. */
        connection: string;

        /** Indicates whether the connection is a social one. */
        isSocial: boolean;

        /** Name of the entity that is authenticating the user, such as Facebook, Google, SAML, or your own provider. */
        provider: string;

        /** User's unique identifier for this connection/provider. */
        user_id: string;

        /** User info associated with the connection. */
        profileData: unknown;
    }[];

    /** IP address associated with the user's last login. */
    last_ip: string;

    /** Timestamp indicating when the user last logged in. If a user is blocked and logs in, the blocked session updates last_login. */
    last_login: string;

    /** Timestamp indicating the last time the user's password was reset/changed. */
    last_password_reset: string;

    /** Number of times the user has logged in. */
    logins_count: number;

    /** List of multi-factor providers with which the user is enrolled. */
    multifactor: string;

    /** The user's full name. */
    name: string;

    /** The user's nickname. */
    nickname: string;

    /** User's notification days to receive an email. */
    notificationDays: number[];

    /** The user's phone number. Only valid for users with SMS connections. */
    phone_number: string;

    /** Indicates whether the user has been verified their phone number. Only valid for users with SMS connections. */
    phone_verified: boolean;

    /** URL pointing to the user's profile picture. */
    picture: string;

    /** Timestamp indicating when the user's profile was last updated/modified. */
    updated_at: string;

    /** (unique) The user's identifier. */
    user_id: string;

    /** Last date the user changed their approval password. */
    lastApprovalPasswordChange: string;

    /** Indicates whether the user has enabled approval password. */
    isApprovalPasswordActive: boolean;
}

/**
 * Expose thunk functions as exports.
 */
export const getUserProfile = createAsyncThunk(`user/get`, thunks.getUserProfile);
export const logoutUser = createAsyncThunk(`user/logout`, thunks.logoutUser);
export const updateUserProfileProtection = createAsyncThunk(
    `${urlUserProfile}/protection`,
    thunks.updateUserProfileProtection,
);
export const updateUserProfileReceiveTasksByEmail = createAsyncThunk(
    `${urlUserProfile}/notifications`,
    thunks.updateUserProfileReceiveTasksByEmail,
);
export const updateUserProfilePicture = createAsyncThunk(
    `${urlUserProfile}/image`,
    thunks.updateUserProfilePicture,
);
export const updateUserApprovalPassword = createAsyncThunk(
    `${urlUserProfile}/workflow/approval`,
    thunks.updateUserApprovalPassword,
);
export const updateUserApprovalPasswordToggle = createAsyncThunk(
    `${urlUserProfile}/workflow/approval/toggle`,
    thunks.updateUserApprovalPasswordToggle,
);
export const resetUserApprovalPassword = createAsyncThunk(
    `${urlUserProfile}/workflow/approval/reset`,
    thunks.resetUserApprovalPassword,
);

/**
 * Create the redux slice.
 */
export const userProfileSlice = createSlice({
    name: 'userProfile',
    extraReducers: thunks._reducers,
    initialState: initialState,
    reducers,
});

/**
 * Export functions from slice actions.
 */
export const {
    resetWorkflowPasswordSuccess,
    resetWorkflowPasswordError,
    resetWorkflowPasswordResetSuccess,
    resetWorkflowPasswordResetError,
    updateUserWfPasswordActive,
    updateUserLastWfPasswordChange,
} = userProfileSlice.actions;

/**
 * Export slice reducer as default.
 */
export default userProfileSlice.reducer;
